<template>
  <Html :lang="head.htmlAttrs.lang" :dir="head.htmlAttrs.dir">
    <div :class="twJoin(width <= 640 && isCartOpen && 'invisible')">
      <div
        id="0"
        :ref="setItemRef"
        :class="twJoin(!visibility[0] && 'invisible')"
      >
        <Suspense><CoverPageSwiperComponent /></Suspense>
      </div>
      <div
        id="1"
        :ref="setItemRef"
        :class="twJoin(!visibility[1] && 'invisible')"
      >
        <Suspense>
          <CollabsSwiperComponent :is-show-content="true" />
        </Suspense>
      </div>
      <Suspense><TwoColumnCardLinkComponent /></Suspense>
      <div
        id="2"
        :ref="setItemRef"
        :class="twJoin('space-y-16 py-16', !visibility[2] && 'invisible')"
      >
        <ProductListSwiperComponent
          v-show="newArrivals.length > 0"
          :title="$t('landing.titleNewProduct')"
          slide-key="product-landing-a"
          :data="newArrivals"
          recommendation
        />
        <ProductListSwiperComponent
          v-show="hotPicks.length > 0"
          :title="$t('landing.titleHotProduct')"
          slide-key="product-landing-b"
          :data="hotPicks"
          recommendation
        />
      </div>
      <div
        id="3"
        :ref="setItemRef"
        :class="twJoin(!visibility[3] && 'invisible')"
      >
        <Suspense><ArticleLinkSwiperComponent /></Suspense>
      </div>
      <div
        id="4"
        :ref="setItemRef"
        :class="twJoin(!visibility[4] && 'invisible')"
      >
        <RecommendProductComponent
          :data="collection"
          :tags="collectionFeaturedTags"
        />
      </div>
      <div
        id="5"
        :ref="setItemRef"
        :class="twJoin(!visibility[5] && 'invisible')"
      >
        <Suspense><SpecialCollectionComponent /></Suspense>
      </div>
      <div
        id="6"
        :ref="setItemRef"
        :class="twJoin(!visibility[6] && 'invisible')"
      >
        <Suspense><CaseClubSocialComponent /></Suspense>
      </div>
    </div>
  </Html>
</template>

<script lang="ts" setup>
import { forEach, toNumber } from 'lodash-es'
import { twJoin } from 'tailwind-merge'
import { cmsRepository } from '~/repository/cmsRepository'
import { seoRepository } from '~/repository/seoRepository'

const CoverPageSwiperComponent = defineAsyncComponent({
  loader: () => import('../components/CoverPageSwiper.vue')
})
const CollabsSwiperComponent = defineAsyncComponent({
  loader: () => import('../components/CollabsSwiper.vue')
})
const TwoColumnCardLinkComponent = defineAsyncComponent({
  loader: () => import('../components/TwoColumnCardLink.vue')
})
const ProductListSwiperComponent = defineAsyncComponent({
  loader: () => import('../components/v2/ProductListSwiper.vue')
})
const ArticleLinkSwiperComponent = defineAsyncComponent({
  loader: () => import('../components/ArticleLinkSwiper.vue')
})

const RecommendProductComponent = defineAsyncComponent({
  loader: () => import('../components/v2/RecommendProduct.vue')
})

const SpecialCollectionComponent = defineAsyncComponent({
  loader: () => import('../components/SpecialCollection.vue')
})

const CaseClubSocialComponent = defineAsyncComponent({
  loader: () => import('../components/CaseClubSocial.vue')
})

useHead({
  meta: [
    {
      name: 'viewport',
      content: 'initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0'
    }
  ]
})
const { locale } = useI18n()
const { $serverApi } = useNuxtApp()
const seoRepo = seoRepository($serverApi)
const visibility = ref<boolean[]>([true, true, true, true, true, true, true])
const webStore = useWebsiteStoreV2()
const { isCartOpen } = storeToRefs(webStore)

const { data: pageData } = await useAsyncData(
  'getStaticSeoLanding',
  async () => {
    const data = await seoRepo.getSeo('landing-page')
    return data
  }
)

const seoTitle =
  locale.value === 'en'
    ? pageData.value?.meta_title?.en
    : pageData.value?.meta_title?.th || pageData.value?.meta_title?.en

const seoDescription =
  locale.value === 'en'
    ? pageData.value?.meta_description?.en
    : pageData.value?.meta_description?.th ||
      pageData.value?.meta_description?.en

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogImage: pageData.value?.og_image,
  keywords: pageData.value?.meta_keyword
})

const isMobile = ref(false)
const { width } = useWindowSize()

const config = useRuntimeConfig()

const observer = ref<IntersectionObserver | null>(null)
const itemRefs = ref<(globalThis.ComponentPublicInstance | Element | null)[]>(
  []
)

const setItemRef = (
  el: globalThis.ComponentPublicInstance | Element | null
) => {
  if (el && !itemRefs.value.includes(el)) {
    itemRefs.value.push(el)
  }
}

const { search: algoliaSearchNewArrivals } = useAlgoliaSearch<AlgoliaProductV2>(
  config.public.algoliaIndexNext
)

const { hits: newArrivals } = await algoliaSearchNewArrivals({
  query: 'collection-new-arrivals',
  requestOptions: { page: 0, hitsPerPage: 20 }
})

const { search: algoliaSearchHotPicks } = useAlgoliaSearch<AlgoliaProductV2>(
  config.public.algoliaIndexNext
)

const { hits: hotPicks } = await algoliaSearchHotPicks({
  query: 'collection-hot-picks',
  requestOptions: { page: 0, hitsPerPage: 20 }
})

const { search: algoliaSearchCollection } = useAlgoliaSearch<AlgoliaProductV2>(
  config.public.algoliaIndexNext
)

const { hits: collection } = await algoliaSearchCollection({
  query: 'collection-featured',
  requestOptions: { page: 0, hitsPerPage: 20 }
})

const cmsRepo = cmsRepository($serverApi)

const { data: collectionFeaturedTags } = await useAsyncData<
  {
    tagsEn: string
    tagsTh?: string
  }[]
>(
  'getLandingPageFestiveProduct',
  async () => {
    const response = await cmsRepo.getWidget('landing-page-festive-product')
    if (response.data.data.items) return response.data.data.items
    return null
  },
  { server: false }
)

const head = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: 'id',
  addSeoAttributes: true
})

watch(
  () => itemRefs.value,
  (newValue) => {
    forEach(newValue, (el) => {
      if (el) {
        observer.value?.observe(el as Element)
      }
    })
  },
  { deep: true }
)

const handleIntersection = (entries: IntersectionObserverEntry[]) => {
  forEach(entries, (entry) => {
    const currentIndex = toNumber(entry.target.getAttribute('id') || -1)
    if (currentIndex !== -1) {
      visibility.value[currentIndex] = entry.isIntersecting
    }
  })
}

useSchemaOrg({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CaseClub Thailand',
  url: config.public.siteUrl,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Thai', 'English']
  },
  sameAs: [
    'https://www.facebook.com/profile.php?id=61556886332382',
    'https://www.instagram.com/caseclub_official/',
    'https://www.tiktok.com/@caseclub_official'
  ]
})

onUnmounted(() => {
  if (observer.value) {
    forEach(itemRefs.value, (el) => observer.value?.unobserve(el as Element))
  }
  observer.value = null
})

onMounted(() => {
  if (width.value < 768) {
    isMobile.value = true
  }
  observer.value = new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: '0px',
    threshold: 0.01
  })
  nextTick(() => {
    forEach(itemRefs.value, (el) => {
      if (el) {
        observer.value?.observe(el as Element)
      }
    })
  })
})
</script>
